import { createContext, useCallback, useEffect, useState } from 'react';
import { supabase } from '../config/supabaseClient';
import { registerUser } from '../services/authApi';

export const AuthContext = createContext();

// Invite and password-reset emails redirect back with a hash like
// #access_token=...&type=invite (or type=recovery). We detect that on first
// load so the app can show the Set-Password screen instead of the calendar.
const hashHasRecoveryType = () => {
  const hash = window.location.hash || '';
  return hash.includes('type=invite') || hash.includes('type=recovery');
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(hashHasRecoveryType());

  useEffect(() => {
    // Seed state with any persisted session.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    // React to sign-in / sign-out / token refresh and the recovery event.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setLoading(false);
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  // Auto-registro por magic link: el backend crea la cuenta SIN confirmar
  // (valida el secreto del link + el dominio) y luego Supabase envía el email
  // de confirmación con resend(). El usuario entra recién al hacer clic en el
  // link del correo (ahí se confirma el email y se establece la sesión).
  const register = useCallback(async (email, password, secret) => {
    await registerUser(email, password, secret);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  // Used to complete an invite or a password reset.
  const setPassword = useCallback(async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    setRecoveryMode(false);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw error;
  }, []);

  const user = session?.user ?? null;

  const contextValue = {
    session,
    user,
    isAdmin: user?.app_metadata?.role === 'admin',
    loading,
    recoveryMode,
    signIn,
    register,
    signOut,
    setPassword,
    requestPasswordReset,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
